import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Bars3Icon, 
  Cog6ToothIcon,
  ArrowPathIcon,
  GlobeAltIcon,
  SunIcon,
  MoonIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { apiClient } from '../utils/api';
import { cn, formatNumber } from '../utils/helpers';
import { Balance, GlobalState } from '../types';
import io, { Socket } from 'socket.io-client';
import { getSocketUrl } from '../utils/api';

interface HeaderProps {
  onToggleSidebar: () => void;
  onResetLayout: () => void;
  onViewChange?: (viewId: string) => void;
  isMenuOpen: boolean;
}

interface SystemStatus {
  isConnected: boolean;
  isTestnet: boolean;
  aiModel: string;
  hasApiKey: boolean;
  telegramEnabled: boolean;
  theme: string;
  botEnabled: boolean;
  exchange: string;
  currentPrice?: number;
}

const Header: React.FC<HeaderProps> = ({ onToggleSidebar, onResetLayout, onViewChange, isMenuOpen }) => {
  const { language, setLanguage, t } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [showProfile, setShowProfile] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showSystemStatus, setShowSystemStatus] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [systemStatus, setSystemStatus] = useState<SystemStatus>({
    isConnected: false,
    isTestnet: true,
    aiModel: 'rule-based',
    hasApiKey: false,
    telegramEnabled: false,
    theme: 'dark',
    botEnabled: false,
    exchange: 'binance'
  });
  const [versionInfo, setVersionInfo] = useState<any>(null);
  const [totalBalance, setTotalBalance] = useState<number>(0);
  const [todayProfit, setTodayProfit] = useState<number>(0);
  const [socket, setSocket] = useState<Socket | null>(null);

  // Responsive handler
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Socket connection
  useEffect(() => {
    const socketInstance = io(getSocketUrl(), {
      path: '/socket.io/',
      transports: ['websocket', 'polling'],
      timeout: 5000,
    });

    socketInstance.on('connect', () => {
      setSystemStatus(prev => ({ ...prev, isConnected: true }));
    });

    socketInstance.on('disconnect', () => {
      setSystemStatus(prev => ({ ...prev, isConnected: false }));
    });

    socketInstance.on('systemStatus', (status: Partial<SystemStatus>) => {
      setSystemStatus(prev => ({ ...prev, ...status }));
    });

    socketInstance.on('balance', (balances: Balance[]) => {
      const total = balances.reduce((sum, b) => sum + (b.totalUSDT || 0), 0);
      setTotalBalance(total);
    });

    socketInstance.on('profitStatus', (data: { todayProfit: number }) => {
      setTodayProfit(data.todayProfit);
    });

    socketInstance.on('price', (data: { price: number }) => {
      setSystemStatus(prev => ({ ...prev, currentPrice: data.price }));
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
    };
  }, []);

  // Fetch version info
  useEffect(() => {
    const fetchVersion = async () => {
      const response = await apiClient.get('/api/version');
      if (response.success && response.data) {
        setVersionInfo(response.data);
      }
    };
    fetchVersion();
  }, []);

  // Handle language change
  const handleLanguageChange = useCallback((lang: string) => {
    setLanguage(lang as any);
    setShowLanguage(false);
  }, [setLanguage]);

  // Profile dropdown
  const profileMenu = useMemo(() => (
    <div className="relative">
      <button
        onClick={() => {
          setShowProfile(!showProfile);
          setShowLanguage(false);
          setShowSystemStatus(false);
        }}
        className={cn(
          'p-2 rounded-lg transition-colors',
          theme === 'dark' 
            ? 'hover:bg-gray-700' 
            : 'hover:bg-gray-100'
        )}
      >
        <UserIcon className="w-5 h-5" />
      </button>
      
      {showProfile && (
        <div className={cn(
          'absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-50',
          theme === 'dark' 
            ? 'bg-gray-800 border border-gray-700' 
            : 'bg-white border border-gray-200'
        )}>
          <div className="p-2">
            <div className="px-3 py-2 text-sm font-semibold">
              {t.profile}
            </div>
            <div className="border-t border-gray-700 my-1" />
            <button
              className={cn(
                'w-full text-left px-3 py-2 text-sm rounded transition-colors',
                theme === 'dark' 
                  ? 'hover:bg-gray-700' 
                  : 'hover:bg-gray-100'
              )}
              onClick={() => onViewChange?.('settings')}
            >
              {t.settings}
            </button>
            <button
              className={cn(
                'w-full text-left px-3 py-2 text-sm rounded transition-colors',
                theme === 'dark' 
                  ? 'hover:bg-gray-700' 
                  : 'hover:bg-gray-100'
              )}
              onClick={() => {
                // Logout logic
                if (window.confirm(t.logoutConfirm)) {
                  // Clear local storage and reload
                  localStorage.clear();
                  window.location.reload();
                }
              }}
            >
              {t.logout}
            </button>
          </div>
        </div>
      )}
    </div>
  ), [showProfile, theme, t, onViewChange]);

  // Language dropdown
  const languageMenu = useMemo(() => (
    <div className="relative">
      <button
        onClick={() => {
          setShowLanguage(!showLanguage);
          setShowProfile(false);
          setShowSystemStatus(false);
        }}
        className={cn(
          'p-2 rounded-lg transition-colors',
          theme === 'dark' 
            ? 'hover:bg-gray-700' 
            : 'hover:bg-gray-100'
        )}
      >
        <GlobeAltIcon className="w-5 h-5" />
      </button>
      
      {showLanguage && (
        <div className={cn(
          'absolute right-0 mt-2 w-48 rounded-lg shadow-lg z-50',
          theme === 'dark' 
            ? 'bg-gray-800 border border-gray-700' 
            : 'bg-white border border-gray-200'
        )}>
          <div className="p-2">
            <button
              onClick={() => handleLanguageChange('en')}
              className={cn(
                'w-full text-left px-3 py-2 text-sm rounded transition-colors',
                language === 'en' && 'font-bold',
                theme === 'dark' 
                  ? 'hover:bg-gray-700' 
                  : 'hover:bg-gray-100'
              )}
            >
              English
            </button>
            <button
              onClick={() => handleLanguageChange('ko')}
              className={cn(
                'w-full text-left px-3 py-2 text-sm rounded transition-colors',
                language === 'ko' && 'font-bold',
                theme === 'dark' 
                  ? 'hover:bg-gray-700' 
                  : 'hover:bg-gray-100'
              )}
            >
              한국어
            </button>
          </div>
        </div>
      )}
    </div>
  ), [showLanguage, language, theme, handleLanguageChange]);

  // System status indicator
  const statusIndicator = useMemo(() => (
    <div className="flex items-center space-x-2">
      {/* Connection status */}
      <div className="relative">
        <div
          className={cn(
            'w-2 h-2 rounded-full',
            systemStatus.isConnected ? 'bg-green-500' : 'bg-red-500'
          )}
        />
        {systemStatus.isConnected && (
          <div className="absolute inset-0 rounded-full bg-green-500 animate-ping" />
        )}
      </div>
      
      {/* Current price */}
      {systemStatus.currentPrice && (
        <span className="text-sm font-medium">
          ${formatNumber(systemStatus.currentPrice, 2)}
        </span>
      )}
      
      {/* Bot status */}
      {systemStatus.botEnabled && (
        <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-500">
          BOT
        </span>
      )}
      
      {/* Testnet indicator */}
      {systemStatus.isTestnet && (
        <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-500">
          TESTNET
        </span>
      )}
    </div>
  ), [systemStatus]);

  // Profit display
  const profitDisplay = useMemo(() => {
    if (todayProfit === 0) return null;
    
    const isProfit = todayProfit > 0;
    return (
      <div className="flex items-center space-x-2">
        <span className="text-sm text-gray-500">{t.todaysProfit}:</span>
        <span className={cn(
          'text-sm font-bold',
          isProfit ? 'text-green-500' : 'text-red-500'
        )}>
          {isProfit ? '+' : ''}{formatNumber(todayProfit, 2)} USDT
        </span>
      </div>
    );
  }, [todayProfit, t]);

  return (
    <header className={cn(
      'flex items-center justify-between p-4 border-b transition-colors',
      theme === 'dark' 
        ? 'bg-gray-900 border-gray-800' 
        : 'bg-white border-gray-200'
    )}>
      {/* Left section */}
      <div className="flex items-center space-x-4">
        <button
          onClick={onToggleSidebar}
          className={cn(
            'p-2 rounded-lg transition-colors',
            theme === 'dark' 
              ? 'hover:bg-gray-800' 
              : 'hover:bg-gray-100'
          )}
        >
          <Bars3Icon className="w-6 h-6" />
        </button>
        
        <h1 className="text-xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 bg-clip-text text-transparent">
          exTrader
        </h1>
        
        {versionInfo && (
          <span className="text-xs text-gray-500">
            v{versionInfo.version}
          </span>
        )}
      </div>

      {/* Center section - Status and profit */}
      {!isMobile && (
        <div className="flex items-center space-x-6">
          {statusIndicator}
          {profitDisplay}
        </div>
      )}

      {/* Right section */}
      <div className="flex items-center space-x-2">
        {/* Reset layout button */}
        {!isMobile && (
          <button
            onClick={onResetLayout}
            className={cn(
              'p-2 rounded-lg transition-colors',
              theme === 'dark' 
                ? 'hover:bg-gray-700' 
                : 'hover:bg-gray-100'
            )}
            title={t.layoutReset}
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        )}
        
        {/* Settings button */}
        <button
          onClick={() => onViewChange?.('settings')}
          className={cn(
            'p-2 rounded-lg transition-colors',
            theme === 'dark' 
              ? 'hover:bg-gray-700' 
              : 'hover:bg-gray-100'
          )}
        >
          <Cog6ToothIcon className="w-5 h-5" />
        </button>
        
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className={cn(
            'p-2 rounded-lg transition-colors',
            theme === 'dark' 
              ? 'hover:bg-gray-700' 
              : 'hover:bg-gray-100'
          )}
        >
          {theme === 'dark' ? (
            <SunIcon className="w-5 h-5" />
          ) : (
            <MoonIcon className="w-5 h-5" />
          )}
        </button>
        
        {/* Language menu */}
        {languageMenu}
        
        {/* Profile menu */}
        {profileMenu}
      </div>

      {/* Mobile status */}
      {isMobile && (
        <div className="absolute bottom-0 left-0 right-0 p-2 border-t border-gray-700 bg-gray-800">
          <div className="flex items-center justify-between text-xs">
            {statusIndicator}
            {profitDisplay}
          </div>
        </div>
      )}
    </header>
  );
};

export default React.memo(Header);